// Cancelling an andon, with a reason. Lifted from the Andon app's
// `CancelSheet`.
//
// WHY THIS SHEET EXISTS.
//
// `cancelled` has been a real stage in the data since the app was written and
// nothing could reach it. So a mistake had exactly two endings: somebody walked
// across the factory to a machine that was running fine, or the raiser waited
// for the andon to breach and then resolved it with a fiction. The second is
// worse — it writes a false SLA record and a false corrective action onto a row
// every report afterwards reads as real — and both teach the floor that the
// board lies.
//
// THE REASON IS REQUIRED, AND IT IS FIVE CHIPS.
//
// "Cancelled" on its own is the 58.3%-blank problem wearing a different hat.
// Each chip is a different finding about a different part of the system —
// sorted-it-myself is an SLA finding, raised-by-mistake is a UI finding,
// already-raised is a finding about whether the floor can see what has been
// called in — and averaged into one count they say nothing at all. The line of
// text under them is optional, because the chip is the finding and the sentence
// is the detail.
//
// ALREADY RAISED PICKS FROM A LIST, IT DOES NOT ASK FOR A NUMBER.
//
// Typing AN00240 on a gloved thumb is the failure mode this app avoids
// everywhere else, and the open andons are already in the floor payload the
// client is holding. Nearest first: the same work centre, then the same pod,
// then everything else — because the andon somebody is duplicating is almost
// always the one next to them.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { andonApi } from '@/api/andon/client';
import type { AlertDetailVM, AndonAlertVM } from '@/api/andon/types';
import { ApiError } from '@/api/http';
import { Icon } from '@/components/ui/Icon';
import { Sheet } from '@/components/ui/Sheet';
import { useT } from '@/i18n/useT';
import { CANCEL_REASONS, type CancelReasonKey } from '@/lib/cancel';
import { useAndonFloor } from '@/queries/andon';
import { useStore } from '@/store';

export function CancelSheet({
  alert,
  open,
  onClose,
  onCancelled,
  preselect = null,
}: {
  alert: AlertDetailVM;
  open: boolean;
  onClose: () => void;
  /** The andon is gone from the board; the screen leaves with it. */
  onCancelled: () => void;
  /**
   * The chip already lit when the sheet opens — "Sorted myself" from the
   * verb row, which IS that chip. Still changeable: the verb is a door, not
   * a decision made on the operator's behalf.
   */
  preselect?: CancelReasonKey | null;
}) {
  const t = useT();
  const qc = useQueryClient();
  const showToast = useStore((s) => s.showToast);
  const showError = useStore((s) => s.showError);
  const [reason, setReason] = useState<CancelReasonKey | null>(preselect);
  const [note, setNote] = useState('');
  const [duplicateOf, setDuplicateOf] = useState<number | null>(null);

  useEffect(() => {
    if (open) setReason(preselect);
  }, [open, preselect]);

  /**
   * The other open andons, for the `duplicate` reason only.
   *
   * Fetched lazily — `enabled` on the reason, not on the sheet — because four
   * of the five chips never need it and the floor read is not free.
   */
  const floor = useAndonFloor('open', open && reason === 'duplicate');

  const candidates = useMemo(() => {
    const all = floor.data?.alerts ?? [];
    // NEAREST FIRST, and "near" means what the operator can see: their own
    // station, then their own pod, then the rest.
    const rank = (a: AndonAlertVM) =>
      a.workcentre && a.workcentre === alert.workcentre ? 0 : a.production && a.production === alert.production ? 1 : 2;
    return all
      .filter((a) => a.id !== alert.id && a.open)
      .sort((a, b) => rank(a) - rank(b) || b.raisedAt - a.raisedAt)
      .slice(0, 8);
  }, [floor.data, alert.id, alert.workcentre, alert.production]);

  const cancel = useMutation({
    mutationFn: () => andonApi.cancel(alert.id, reason!, note.trim() || null, reason === 'duplicate' ? duplicateOf : null),
    onSuccess: () => {
      showToast(`${alert.qa} ${t.cancelledToastT}`);
      // Every list this andon was on, this app's own included.
      void qc.invalidateQueries({ queryKey: ['andons'] });
      close();
      onCancelled();
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : t.cancelFailedT),
  });

  const close = () => {
    setReason(preselect);
    setNote('');
    setDuplicateOf(null);
    onClose();
  };

  const owner = alert.owner.unowned ? null : alert.owner.name;

  return (
    <Sheet open={open} onClose={close} title={`${t.cancelT} ${alert.qa}?`}>
      <p style={{ margin: '0 0 14px', font: '500 12px/1.6 Manrope,sans-serif', color: 'var(--sub)' }}>
        {t.cancelLeavesBoardT}
        {/* Only when somebody has taken it. On an unowned andon there is nobody
            walking over, and inventing a reassurance about a person who does
            not exist is worse than saying less. */}
        {owner ? (
          <>
            {' '}
            <span style={{ color: 'var(--ink)' }}>
              {owner} {t.cancelOwnerOnWayT}
            </span>{' '}
            — {t.cancelTheyWillBeToldT}
          </>
        ) : null}
      </p>

      <div style={{ font: "700 9px 'Space Grotesk',sans-serif", letterSpacing: '.1em', color: 'var(--sub)', marginBottom: 9, textTransform: 'uppercase' }}>{t.cancelWhyRequiredT}</div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 11 }}>
        {CANCEL_REASONS.map((r) => {
          const on = reason === r.key;
          return (
            <button
              key={r.key}
              type="button"
              className="press-soft"
              aria-pressed={on}
              onClick={() => {
                setReason(r.key);
                // Dropping the link when the reason changes away from
                // duplicate. Odoo ignores it anyway, but a stale id sitting in
                // state is a stale id somebody re-selects duplicate and sends
                // without noticing.
                if (r.key !== 'duplicate') setDuplicateOf(null);
              }}
              style={{
                minHeight: 36,
                padding: '6px 13px',
                borderRadius: 11,
                background: on ? 'var(--danger)' : 'var(--surface)',
                border: `1px solid ${on ? 'var(--danger)' : 'var(--line)'}`,
                color: on ? '#fff' : 'var(--ink)',
                font: '600 12.5px Manrope,sans-serif',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {t[r.label]}
            </button>
          );
        })}
      </div>

      {reason === 'duplicate' && (
        <div style={{ marginBottom: 11 }}>
          <div style={{ font: "700 9px 'Space Grotesk',sans-serif", letterSpacing: '.1em', color: 'var(--sub)', marginBottom: 7, textTransform: 'uppercase' }}>{t.cancelWhichOneT}</div>
          {floor.isPending ? (
            <p style={{ margin: 0, font: '500 12px Manrope,sans-serif', color: 'var(--sub)' }}>{t.cancelLookingT}</p>
          ) : candidates.length === 0 ? (
            <p style={{ margin: 0, font: '500 12px/1.5 Manrope,sans-serif', color: 'var(--sub)' }}>{t.cancelNothingElseOpenT}</p>
          ) : (
            <div style={{ borderRadius: 13, background: 'var(--surface)', border: '1px solid var(--line)', overflow: 'hidden' }}>
              {candidates.map((c) => {
                const on = duplicateOf === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    aria-pressed={on}
                    // Tapping the chosen one again clears it, because the link
                    // is optional and there has to be a way back out of a
                    // mis-tap that does not involve changing the reason.
                    onClick={() => setDuplicateOf(on ? null : c.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '10px 12px', background: on ? 'var(--soft)' : 'transparent', border: 0, borderTop: '1px solid var(--line)', textAlign: 'left', cursor: 'pointer' }}
                  >
                    <span className="numeric" style={{ font: "700 11.5px 'Space Grotesk',sans-serif", color: on ? 'var(--ink)' : 'var(--sub)', flex: 'none' }}>
                      {c.qa}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', font: '600 12.5px Manrope,sans-serif', color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</span>
                      <span style={{ display: 'block', font: '500 10.5px Manrope,sans-serif', color: 'var(--sub)', marginTop: 1 }}>
                        {[c.workcentre, c.production].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    {on && <Icon name="check" size={15} strokeWidth={3} style={{ color: 'var(--pos)' }} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, 500))}
        rows={2}
        placeholder={t.cancelNotePhT}
        aria-label={t.cancelNotePhT}
        style={{ width: '100%', padding: '11px 13px', borderRadius: 13, background: 'var(--surface)', border: '1px solid var(--line)', color: 'var(--ink)', font: '500 13px/1.5 Manrope,sans-serif', resize: 'none', outline: 'none', marginBottom: 12 }}
      />

      <div style={{ display: 'flex', gap: 9 }}>
        <button
          type="button"
          className="press-soft"
          onClick={close}
          style={{ flex: 1, height: 52, borderRadius: 15, background: 'var(--surface)', border: '1px solid var(--line)', color: 'var(--ink)', font: "700 14px 'Space Grotesk',sans-serif" }}
        >
          {t.cancelKeepOpenT}
        </button>
        <button
          type="button"
          className="press-soft"
          disabled={!reason || cancel.isPending}
          onClick={() => cancel.mutate()}
          style={{
            flex: 1.3,
            height: 52,
            borderRadius: 15,
            // Inert until a reason is picked, rather than hidden or absent: the
            // button being there and not working is what says a reason is
            // required, without a validation message nobody reads.
            background: reason ? 'var(--danger)' : 'var(--surface2)',
            color: reason ? '#fff' : 'var(--sub)',
            border: 0,
            font: "700 14px 'Space Grotesk',sans-serif",
            opacity: cancel.isPending ? 0.6 : 1,
          }}
        >
          {cancel.isPending ? t.cancellingT : t.cancelTheAndonT}
        </button>
      </div>
      <p style={{ margin: '10px 2px 0', textAlign: 'center', font: '500 11px/1.5 Manrope,sans-serif', color: 'var(--sub)' }}>{t.cancelPostsNothingDeletedT}</p>
    </Sheet>
  );
}
